import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import axios from "axios";
import moment from "moment";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faBan,
  faCheck,
  faComments,
  faCopy,
  faEllipsisVertical,
  faEnvelope,
  faPaperPlane,
  faPaperclip,
  faPen,
  faTimes,
  faTrash,
} from "@fortawesome/free-solid-svg-icons";
import API from "../Api/API";
import AuthService from "../AuthService/AuthService";
import { showNotification } from "../Notification/Notification";
import ChatAttachment, {
  ALLOWED_EXTENSIONS,
  INLINE_IMAGE_EXTENSIONS,
  MAX_ATTACHMENT_SIZE,
  fileIcon,
  formatSize,
  getExtension,
} from "./ChatAttachment";
import "./Chat.css";

const MAX_LENGTH = 2000;
const PAGE_SIZE = 50;
const PING_INTERVAL_MS = 25000;
// No event (not even a pong) for this long means the connection is dead
const HEARTBEAT_TIMEOUT_MS = 60000;
const TYPING_SEND_INTERVAL_MS = 2500;
const TYPING_SHOW_MS = 4000;
// Must match ChatService.EDIT_WINDOW
const EDIT_WINDOW_MS = 15 * 60 * 1000;
const MENU_WIDTH = 180;

const buildSocketUrl = () => {
  const url = new URL(API.url, window.location.href);
  url.protocol = url.protocol === "https:" ? "wss:" : "ws:";
  url.pathname = url.pathname.replace(/\/?$/, "/") + "api/chat/ws";
  return url.toString();
};

const escapeHtml = (text) =>
  text.replace(
    /[&<>"']/g,
    (c) =>
      ({
        "&": "&amp;",
        "<": "&lt;",
        ">": "&gt;",
        '"': "&quot;",
        "'": "&#39;",
      })[c]
  );

const initials = (contact) =>
  `${(contact.firstName || "").charAt(0)}${(contact.lastName || "").charAt(0)}`.toUpperCase();

const fullName = (contact) => `${contact.firstName} ${contact.lastName}`;

const partnerOf = (message, myId) =>
  message.senderId === myId ? message.recipientId : message.senderId;

const ACCEPT_ATTRIBUTE = ALLOWED_EXTENSIONS.map((ext) => "." + ext).join(",");

const appendUnique = (list, message) =>
  list.some((m) => m.id === message.id) ? list : [...list, message];

const canEdit = (message) =>
  !message.deleted &&
  Date.now() - new Date(message.createdAt).getTime() < EDIT_WINDOW_MS;

const ChatWidget = ({ isIndividual }) => {
  const { t } = useTranslation(["chat"]);
  const myId = AuthService.getUserId();

  const [open, setOpen] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState({});
  const [hasMore, setHasMore] = useState({});
  const [typing, setTyping] = useState({});
  const [connected, setConnected] = useState(false);
  const [connectionEpoch, setConnectionEpoch] = useState(0);
  const [visible, setVisible] = useState(
    document.visibilityState === "visible"
  );
  const [draft, setDraft] = useState("");
  const [sending, setSending] = useState(false);
  const [sendError, setSendError] = useState(null);
  const [filter, setFilter] = useState("");
  const [pendingFile, setPendingFile] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [menu, setMenu] = useState(null);
  const [editing, setEditing] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const socketRef = useRef(null);
  const contactsRef = useRef(contacts);
  const viewingRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const typingTimersRef = useRef({});
  const listRef = useRef(null);
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const panelRef = useRef(null);
  const widgetRef = useRef(null);
  const scrollRestoreRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const loadingOlderRef = useRef(false);

  contactsRef.current = contacts;
  // Conversation the user is actually looking at (open panel, focused tab)
  viewingRef.current = open && visible ? activeId : null;

  const activeContact = contacts.find((c) => c.userId === activeId) || null;
  const activeMessages = (activeId && messages[activeId]) || [];
  const totalUnread = contacts.reduce((sum, c) => sum + c.unreadCount, 0);
  const enabled = isIndividual || contacts.length > 0;
  const coach = isIndividual ? contacts.find((c) => c.coach) : null;

  const fetchContacts = useCallback(() => {
    return axios
      .get(API.url + "api/chat/contacts", { withCredentials: true })
      .then((res) => {
        setContacts(res.data);
        setContactsLoaded(true);
        if (isIndividual) {
          const coachContact = res.data.find((c) => c.coach) || res.data[0];
          setActiveId(coachContact ? coachContact.userId : null);
        }
      })
      .catch(() => setContactsLoaded(true));
  }, [isIndividual]);

  const fetchMessages = useCallback((partnerId, beforeId) => {
    const params = beforeId ? { beforeId } : {};
    return axios
      .get(API.url + "api/chat/messages/" + partnerId, {
        params,
        withCredentials: true,
      })
      .then((res) => {
        setMessages((prev) => {
          const current = beforeId ? prev[partnerId] || [] : [];
          const known = new Set(current.map((m) => m.id));
          const older = res.data.filter((m) => !known.has(m.id));
          return { ...prev, [partnerId]: [...older, ...current] };
        });
        setHasMore((prev) => ({
          ...prev,
          [partnerId]: res.data.length === PAGE_SIZE,
        }));
      });
  }, []);

  const markAsRead = useCallback((partnerId) => {
    setContacts((prev) =>
      prev.map((c) => (c.userId === partnerId ? { ...c, unreadCount: 0 } : c))
    );
    axios
      .put(API.url + "api/chat/read/" + partnerId, null, {
        withCredentials: true,
      })
      .catch(() => {});
  }, []);

  const replaceMessage = useCallback(
    (message) => {
      const partnerId = partnerOf(message, myId);
      setMessages((prev) =>
        prev[partnerId]
          ? {
              ...prev,
              [partnerId]: prev[partnerId].map((m) =>
                m.id === message.id ? message : m
              ),
            }
          : prev
      );
      setContacts((prev) =>
        prev.map((c) =>
          c.userId === partnerId &&
          c.lastMessage &&
          c.lastMessage.id === message.id
            ? { ...c, lastMessage: message }
            : c
        )
      );
    },
    [myId]
  );

  const removeMessage = useCallback(
    (messageId) => {
      setMessages((prev) => {
        const next = {};
        Object.keys(prev).forEach((key) => {
          next[key] = prev[key].filter((m) => m.id !== messageId);
        });
        return next;
      });
      // The previous message becomes the last one in the contact list
      if (
        contactsRef.current.some(
          (c) => c.lastMessage && c.lastMessage.id === messageId
        )
      ) {
        fetchContacts();
      }
    },
    [fetchContacts]
  );

  const handleSocketEvent = useCallback(
    (event) => {
      switch (event.type) {
        case "message": {
          const message = event.message;
          const partnerId = partnerOf(message, myId);
          const incoming = message.senderId !== myId;
          const contact = contactsRef.current.find(
            (c) => c.userId === partnerId
          );
          if (!contact) {
            fetchContacts();
            return;
          }
          const isViewing = viewingRef.current === partnerId;

          setMessages((prev) =>
            prev[partnerId]
              ? { ...prev, [partnerId]: appendUnique(prev[partnerId], message) }
              : prev
          );
          setContacts((prev) =>
            prev.map((c) =>
              c.userId === partnerId
                ? {
                    ...c,
                    lastMessage: message,
                    unreadCount:
                      incoming && !isViewing ? c.unreadCount + 1 : c.unreadCount,
                  }
                : c
            )
          );

          if (incoming) {
            setTyping((prev) => ({ ...prev, [partnerId]: false }));
            if (isViewing) {
              markAsRead(partnerId);
            } else {
              showNotification(
                escapeHtml(t("new_message_from", { name: fullName(contact) }))
              );
            }
          }
          break;
        }
        case "updated": {
          const message = event.message;
          replaceMessage(message);
          // A deleted unread message no longer counts as unread
          if (message.deleted && message.senderId !== myId && !message.readAt) {
            fetchContacts();
          }
          break;
        }
        case "hidden": {
          removeMessage(event.messageId);
          break;
        }
        case "read": {
          const now = new Date().toISOString();
          setMessages((prev) =>
            prev[event.userId]
              ? {
                  ...prev,
                  [event.userId]: prev[event.userId].map((m) =>
                    m.senderId === myId && !m.readAt ? { ...m, readAt: now } : m
                  ),
                }
              : prev
          );
          break;
        }
        case "presence": {
          if (!contactsRef.current.some((c) => c.userId === event.userId)) {
            fetchContacts();
            return;
          }
          setContacts((prev) =>
            prev.map((c) =>
              c.userId === event.userId ? { ...c, online: event.online } : c
            )
          );
          if (!event.online) {
            setTyping((prev) => ({ ...prev, [event.userId]: false }));
          }
          break;
        }
        case "typing": {
          const partnerId = event.userId;
          setTyping((prev) => ({ ...prev, [partnerId]: true }));
          clearTimeout(typingTimersRef.current[partnerId]);
          typingTimersRef.current[partnerId] = setTimeout(
            () => setTyping((prev) => ({ ...prev, [partnerId]: false })),
            TYPING_SHOW_MS
          );
          break;
        }
        default:
          break;
      }
    },
    [myId, fetchContacts, markAsRead, replaceMessage, removeMessage, t]
  );

  const handleSocketEventRef = useRef(handleSocketEvent);
  handleSocketEventRef.current = handleSocketEvent;

  // Initial contacts
  useEffect(() => {
    fetchContacts();
  }, [fetchContacts]);

  const fetchContactsRef = useRef(fetchContacts);
  fetchContactsRef.current = fetchContacts;

  // Live connection with automatic reconnect
  useEffect(() => {
    if (!enabled) return undefined;

    let socket = null;
    let pingTimer = null;
    let reconnectTimer = null;
    let attempt = 0;
    let disposed = false;

    const connect = () => {
      const current = new WebSocket(buildSocketUrl());
      socket = current;
      socketRef.current = current;

      // A replaced socket closes asynchronously, possibly after the new one
      // opened, so its handlers must not touch the state anymore
      const isStale = () => disposed || socket !== current;
      let lastSeen = Date.now();

      current.onopen = () => {
        if (isStale()) return;
        attempt = 0;
        setConnected(true);
        setConnectionEpoch((value) => value + 1);
        // Resync anything missed while disconnected
        fetchContactsRef.current();
        clearInterval(pingTimer);
        lastSeen = Date.now();
        pingTimer = setInterval(() => {
          if (Date.now() - lastSeen > HEARTBEAT_TIMEOUT_MS) {
            // Half-open connection (sleep, network change) - reconnect
            current.close();
            current.onclose();
            return;
          }
          if (current.readyState === WebSocket.OPEN) {
            current.send(JSON.stringify({ type: "ping" }));
          }
        }, PING_INTERVAL_MS);
      };

      current.onmessage = (e) => {
        if (isStale()) return;
        lastSeen = Date.now();
        try {
          handleSocketEventRef.current(JSON.parse(e.data));
        } catch (err) {
          console.error("Invalid chat event", err);
        }
      };

      current.onclose = () => {
        if (isStale()) return;
        // Detach so a late close event of this socket is ignored
        socket = null;
        clearInterval(pingTimer);
        setConnected(false);
        const delay = Math.min(30000, 1000 * 2 ** attempt);
        attempt += 1;
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      disposed = true;
      clearInterval(pingTimer);
      clearTimeout(reconnectTimer);
      if (socket) socket.close();
      socketRef.current = null;
    };
  }, [enabled]);

  useEffect(() => {
    const onVisibilityChange = () =>
      setVisible(document.visibilityState === "visible");
    document.addEventListener("visibilitychange", onVisibilityChange);
    return () =>
      document.removeEventListener("visibilitychange", onVisibilityChange);
  }, []);

  useEffect(() => {
    const timers = typingTimersRef.current;
    return () => Object.values(timers).forEach(clearTimeout);
  }, []);

  // Lift the widget above the page footer while the footer is on screen
  useEffect(() => {
    if (!contactsLoaded || !enabled) return undefined;
    let frame = null;
    const update = () => {
      frame = null;
      const widget = widgetRef.current;
      const footer = document.querySelector("footer.footer");
      if (!widget) return;
      const lift = footer
        ? Math.max(0, window.innerHeight - footer.getBoundingClientRect().top)
        : 0;
      widget.style.setProperty("--chat-lift", `${Math.round(lift)}px`);
    };
    const schedule = () => {
      if (frame === null) frame = requestAnimationFrame(update);
    };
    update();
    // Capture phase catches scrolling of any container, not only the window
    document.addEventListener("scroll", schedule, { capture: true, passive: true });
    window.addEventListener("resize", schedule);
    // Page height changes when content loads or the route changes
    const resizeObserver = new ResizeObserver(schedule);
    resizeObserver.observe(document.body);
    return () => {
      document.removeEventListener("scroll", schedule, { capture: true });
      window.removeEventListener("resize", schedule);
      resizeObserver.disconnect();
      if (frame !== null) cancelAnimationFrame(frame);
    };
  }, [contactsLoaded, enabled]);

  // Refresh contacts when the panel is opened (e.g. coach changed via e-mail modal)
  useEffect(() => {
    if (open) fetchContacts();
  }, [open, fetchContacts]);

  // Load the conversation when it is shown, reload after (re)connect
  useEffect(() => {
    if (open && activeId) {
      stickToBottomRef.current = true;
      fetchMessages(activeId).catch(() => {});
    }
  }, [open, activeId, connectionEpoch, fetchMessages]);

  useEffect(() => {
    if (
      open &&
      visible &&
      activeContact &&
      activeContact.unreadCount > 0
    ) {
      markAsRead(activeContact.userId);
    }
  }, [open, visible, activeContact, markAsRead]);

  useEffect(() => {
    setDraft("");
    setSendError(null);
    setPendingFile(null);
    setEditing(null);
    setMenu(null);
    setConfirmDelete(null);
  }, [activeId]);

  // Close the message menu on outside click / Escape
  useEffect(() => {
    if (!menu) return undefined;
    const close = (e) => {
      if (e.type === "keydown" && e.key !== "Escape") return;
      // The ⋮ button toggles the menu itself
      if (
        e.type === "mousedown" &&
        e.target.closest(".chat-menu, .chat-message-actions")
      )
        return;
      setMenu(null);
    };
    document.addEventListener("mousedown", close);
    document.addEventListener("keydown", close);
    return () => {
      document.removeEventListener("mousedown", close);
      document.removeEventListener("keydown", close);
    };
  }, [menu]);

  // Local preview of a picked image, released when the file changes
  useEffect(() => {
    return () => {
      if (pendingFile && pendingFile.previewUrl) {
        URL.revokeObjectURL(pendingFile.previewUrl);
      }
    };
  }, [pendingFile]);

  // Keep scroll position when older messages are prepended, otherwise follow new ones
  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list) return;
    if (scrollRestoreRef.current !== null) {
      list.scrollTop = list.scrollHeight - scrollRestoreRef.current;
      scrollRestoreRef.current = null;
    } else if (stickToBottomRef.current) {
      list.scrollTop = list.scrollHeight;
    }
  }, [activeMessages, typing, open]);

  const handleScroll = () => {
    const list = listRef.current;
    if (!list) return;
    if (menu) setMenu(null);
    stickToBottomRef.current =
      list.scrollHeight - list.scrollTop - list.clientHeight < 80;

    if (
      list.scrollTop < 40 &&
      hasMore[activeId] &&
      !loadingOlderRef.current &&
      activeMessages.length > 0
    ) {
      loadingOlderRef.current = true;
      scrollRestoreRef.current = list.scrollHeight - list.scrollTop;
      fetchMessages(activeId, activeMessages[0].id)
        .catch(() => {
          scrollRestoreRef.current = null;
        })
        .finally(() => {
          loadingOlderRef.current = false;
        });
    }
  };

  const sendTyping = () => {
    const socket = socketRef.current;
    const now = Date.now();
    if (
      socket &&
      socket.readyState === WebSocket.OPEN &&
      now - lastTypingSentRef.current > TYPING_SEND_INTERVAL_MS
    ) {
      lastTypingSentRef.current = now;
      socket.send(JSON.stringify({ type: "typing", to: activeId }));
    }
  };

  const resizeTextarea = () => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = "auto";
    // border-box height includes the border, scrollHeight does not
    const border = el.offsetHeight - el.clientHeight;
    el.style.height = Math.min(el.scrollHeight + border, 120) + "px";
    el.style.overflowY = el.scrollHeight + border > 120 ? "auto" : "hidden";
  };

  const scrollToBottomIfSticky = () => {
    const list = listRef.current;
    if (list && stickToBottomRef.current) list.scrollTop = list.scrollHeight;
  };

  const selectFile = (file) => {
    if (!file) return;
    if (!ALLOWED_EXTENSIONS.includes(getExtension(file.name))) {
      setSendError("file_type_not_allowed");
      return;
    }
    if (file.size > MAX_ATTACHMENT_SIZE) {
      setSendError("file_too_large");
      return;
    }
    if (file.size === 0) {
      setSendError("file_empty");
      return;
    }
    setSendError(null);
    setPendingFile({
      file,
      previewUrl: INLINE_IMAGE_EXTENSIONS.includes(getExtension(file.name))
        ? URL.createObjectURL(file)
        : null,
    });
    if (textareaRef.current) textareaRef.current.focus();
  };

  const handleFileInputChange = (e) => {
    selectFile(e.target.files[0]);
    // Allow picking the same file again after removing it
    e.target.value = "";
  };

  const handlePaste = (e) => {
    const file = e.clipboardData && e.clipboardData.files[0];
    if (file && !editing) {
      e.preventDefault();
      selectFile(file);
    }
  };

  const handleDragOver = (e) => {
    if (!activeContact || editing || !e.dataTransfer.types.includes("Files"))
      return;
    e.preventDefault();
    setDragActive(true);
  };

  const handleDragLeave = (e) => {
    if (!e.currentTarget.contains(e.relatedTarget)) setDragActive(false);
  };

  const handleDrop = (e) => {
    if (!activeContact || editing) return;
    e.preventDefault();
    setDragActive(false);
    selectFile(e.dataTransfer.files[0]);
  };

  const toggleMenu = (e, message) => {
    if (menu && menu.message.id === message.id) {
      setMenu(null);
      return;
    }
    const panel = panelRef.current;
    if (!panel) return;
    const button = e.currentTarget.getBoundingClientRect();
    const box = panel.getBoundingClientRect();
    const mine = message.senderId === myId;
    const itemCount = 1 + (mine && canEdit(message) ? 1 : 0) +
      (!message.deleted && message.text ? 1 : 0);
    const menuHeight = 12 + itemCount * 38;
    const clamp = (value, max) => Math.min(Math.max(8, value), max);

    // Below the button if it fits above the composer, otherwise above; always inside the panel
    const listBottom = listRef.current
      ? listRef.current.getBoundingClientRect().bottom - box.top
      : box.height;
    const below = button.bottom - box.top + 4;
    const above = button.top - box.top - menuHeight - 4;
    const top = below + menuHeight <= listBottom - 4 ? below : above;
    // Own messages are on the right, so the menu opens to the left of the button
    const left = mine ? button.right - box.left - MENU_WIDTH : button.left - box.left;

    setMenu({
      message,
      top: clamp(top, box.height - menuHeight - 8),
      left: clamp(left, box.width - MENU_WIDTH - 8),
    });
  };

  const startEdit = (message) => {
    setMenu(null);
    setPendingFile(null);
    setSendError(null);
    setEditing(message);
    setDraft(message.text);
    requestAnimationFrame(() => {
      resizeTextarea();
      const el = textareaRef.current;
      if (el) {
        el.focus();
        el.setSelectionRange(el.value.length, el.value.length);
      }
    });
  };

  const cancelEdit = () => {
    setEditing(null);
    setDraft("");
    setSendError(null);
    requestAnimationFrame(resizeTextarea);
  };

  const copyMessage = (message) => {
    setMenu(null);
    if (navigator.clipboard) {
      navigator.clipboard
        .writeText(message.text)
        .then(() => showNotification(escapeHtml(t("copied"))))
        .catch(() => {});
    }
  };

  const deleteMessage = (message, scope) => {
    setConfirmDelete(null);
    axios
      .delete(API.url + "api/chat/message/" + message.id, {
        params: { scope },
        withCredentials: true,
      })
      .then((res) => {
        if (scope === "everyone") {
          replaceMessage(res.data);
        } else {
          removeMessage(message.id);
        }
        if (editing && editing.id === message.id) cancelEdit();
      })
      .catch(() => setSendError("delete_failed"));
  };

  const handleDraftChange = (e) => {
    setDraft(e.target.value.slice(0, MAX_LENGTH));
    setSendError(null);
    resizeTextarea();
    if (e.target.value && !editing) sendTyping();
  };

  const handleSend = (e) => {
    if (e) e.preventDefault();
    const text = draft.trim();
    if (editing) {
      saveEdit(text);
      return;
    }
    if ((!text && !pendingFile) || sending || !activeId) return;

    const partnerId = activeId;
    let request;
    if (pendingFile) {
      const formData = new FormData();
      formData.append("file", pendingFile.file);
      if (text) formData.append("text", text);
      setUploadProgress(0);
      request = axios.post(
        API.url + "api/chat/messages/" + partnerId + "/attachment",
        formData,
        {
          withCredentials: true,
          onUploadProgress: (event) =>
            event.total &&
            setUploadProgress(Math.round((event.loaded / event.total) * 100)),
        }
      );
    } else {
      request = axios.post(
        API.url + "api/chat/messages/" + partnerId,
        { text },
        { withCredentials: true }
      );
    }

    setSending(true);
    request
      .then((res) => {
        const message = res.data;
        stickToBottomRef.current = true;
        setMessages((prev) => ({
          ...prev,
          [partnerId]: appendUnique(prev[partnerId] || [], message),
        }));
        setContacts((prev) =>
          prev.map((c) =>
            c.userId === partnerId ? { ...c, lastMessage: message } : c
          )
        );
        setDraft("");
        setPendingFile(null);
        lastTypingSentRef.current = 0;
        requestAnimationFrame(resizeTextarea);
      })
      .catch((err) => {
        const status = err.response && err.response.status;
        setSendError(
          status === 413
            ? "file_too_large"
            : status === 415
              ? "file_type_not_allowed"
              : "send_failed"
        );
      })
      .finally(() => {
        setSending(false);
        setUploadProgress(null);
        if (textareaRef.current) textareaRef.current.focus();
      });
  };

  const saveEdit = (text) => {
    if (sending) return;
    if (!text && !editing.attachment) return;
    if (text === editing.text) {
      cancelEdit();
      return;
    }
    setSending(true);
    axios
      .put(
        API.url + "api/chat/message/" + editing.id,
        { text },
        { withCredentials: true }
      )
      .then((res) => {
        replaceMessage(res.data);
        cancelEdit();
      })
      .catch((err) => {
        const status = err.response && err.response.status;
        setSendError(status === 409 ? "edit_expired" : "send_failed");
      })
      .finally(() => {
        setSending(false);
        if (textareaRef.current) textareaRef.current.focus();
      });
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
      handleSend(e);
    } else if (e.key === "Escape" && editing) {
      e.stopPropagation();
      cancelEdit();
    }
  };

  const openEmailContact = () => {
    setOpen(false);
    const modal = document.getElementById("global_coach_contact");
    if (modal) modal.style.display = "block";
  };

  const formatDay = (iso) => {
    const day = moment(iso);
    if (day.isSame(moment(), "day")) return t("today");
    if (day.isSame(moment().subtract(1, "day"), "day")) return t("yesterday");
    return day.format("D. M. YYYY");
  };

  const formatListTime = (iso) => {
    const time = moment(iso);
    return time.isSame(moment(), "day")
      ? time.format("HH:mm")
      : time.format("D. M.");
  };

  const filteredContacts = useMemo(() => {
    const query = filter.trim().toLowerCase();
    const list = query
      ? contacts.filter((c) => fullName(c).toLowerCase().includes(query))
      : contacts;
    return [...list].sort((a, b) => {
      if (b.unreadCount !== a.unreadCount) return b.unreadCount - a.unreadCount;
      const aTime = a.lastMessage ? a.lastMessage.createdAt : "";
      const bTime = b.lastMessage ? b.lastMessage.createdAt : "";
      if (aTime !== bTime) return bTime.localeCompare(aTime);
      return fullName(a).localeCompare(fullName(b));
    });
  }, [contacts, filter]);

  if (!contactsLoaded || !enabled) return null;

  const lastOwnMessageId = [...activeMessages]
    .reverse()
    .find((m) => m.senderId === myId)?.id;

  const previewText = (message) => {
    if (message.deleted) return "🚫 " + t("message_deleted");
    if (message.text) return message.text;
    if (!message.attachment) return "";
    return message.attachment.image
      ? "📷 " + t("photo")
      : "📎 " + message.attachment.name;
  };

  const renderAvatar = (contact, size = "") => (
    <div className={`chat-avatar ${size}`}>
      {initials(contact)}
      <span
        className={`chat-presence-dot ${contact.online ? "online" : ""}`}
        title={contact.online ? t("online") : t("offline")}
      />
    </div>
  );

  const renderStatus = (contact) => {
    if (!connected) return t("connecting");
    if (typing[contact.userId]) return t("typing");
    return contact.online ? t("online") : t("offline");
  };

  const renderHeader = () => {
    if (activeContact) {
      return (
        <div className="chat-header">
          {!isIndividual && (
            <button
              type="button"
              className="chat-icon-button"
              onClick={() => setActiveId(null)}
              aria-label={t("back")}
            >
              <FontAwesomeIcon icon={faArrowLeft} />
            </button>
          )}
          {renderAvatar(activeContact)}
          <div className="chat-header-text">
            <div className="chat-header-name">{fullName(activeContact)}</div>
            <div
              className={`chat-header-status ${
                activeContact.online && connected ? "online" : ""
              }`}
            >
              {renderStatus(activeContact)}
            </div>
          </div>
          <button
            type="button"
            className="chat-icon-button"
            onClick={() => setOpen(false)}
            aria-label={t("close")}
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>
      );
    }
    return (
      <div className="chat-header">
        <FontAwesomeIcon icon={faComments} className="chat-header-icon" />
        <div className="chat-header-text">
          <div className="chat-header-name">
            {isIndividual ? t("chat_with_coach") : t("chat_with_clients")}
          </div>
          {!connected && (
            <div className="chat-header-status">{t("connecting")}</div>
          )}
        </div>
        <button
          type="button"
          className="chat-icon-button"
          onClick={() => setOpen(false)}
          aria-label={t("close")}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </div>
    );
  };

  const renderContactList = () => (
    <div className="chat-contacts">
      {contacts.length > 6 && (
        <input
          className="chat-search"
          type="search"
          placeholder={t("search")}
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      )}
      {filteredContacts.map((contact) => (
        <button
          type="button"
          key={contact.userId}
          className="chat-contact"
          onClick={() => setActiveId(contact.userId)}
        >
          {renderAvatar(contact)}
          <div className="chat-contact-body">
            <div className="chat-contact-top">
              <span className="chat-contact-name">{fullName(contact)}</span>
              {contact.lastMessage && (
                <span className="chat-contact-time">
                  {formatListTime(contact.lastMessage.createdAt)}
                </span>
              )}
            </div>
            <div className="chat-contact-bottom">
              <span
                className={`chat-contact-preview ${
                  contact.unreadCount > 0 ? "unread" : ""
                }`}
              >
                {typing[contact.userId]
                  ? t("typing")
                  : contact.lastMessage
                    ? (contact.lastMessage.senderId === myId
                        ? t("you") + ": "
                        : "") + previewText(contact.lastMessage)
                    : t("no_messages_yet")}
              </span>
              {contact.unreadCount > 0 && (
                <span className="chat-badge">{contact.unreadCount}</span>
              )}
            </div>
          </div>
        </button>
      ))}
      {filteredContacts.length === 0 && (
        <div className="chat-empty">{t("no_results")}</div>
      )}
    </div>
  );

  const renderMessages = () => {
    let lastDay = null;
    return (
      <div className="chat-messages" ref={listRef} onScroll={handleScroll}>
        {isIndividual && activeContact && !activeContact.online && (
          <div className="chat-offline-hint">{t("coach_offline_hint")}</div>
        )}
        {activeMessages.length === 0 && (
          <div className="chat-empty">
            {t(isIndividual ? "empty_conversation_coach" : "empty_conversation")}
          </div>
        )}
        {activeMessages.map((message) => {
          const day = moment(message.createdAt).format("YYYY-MM-DD");
          const showDay = day !== lastDay;
          lastDay = day;
          const mine = message.senderId === myId;
          return (
            <React.Fragment key={message.id}>
              {showDay && (
                <div className="chat-day">
                  <span>{formatDay(message.createdAt)}</span>
                </div>
              )}
              <div
                className={`chat-message ${mine ? "mine" : "theirs"} ${
                  menu && menu.message.id === message.id ? "menu-open" : ""
                } ${editing && editing.id === message.id ? "editing" : ""}`}
              >
                <div className="chat-message-row">
                  <div className="chat-message-content">
                    {message.deleted ? (
                      <div className="chat-bubble deleted">
                        <FontAwesomeIcon icon={faBan} />
                        {mine ? t("you_deleted") : t("message_deleted")}
                      </div>
                    ) : (
                      <>
                        {message.attachment && (
                          <ChatAttachment
                            messageId={message.id}
                            attachment={message.attachment}
                            onImageLoad={scrollToBottomIfSticky}
                          />
                        )}
                        {message.text && (
                          <div className="chat-bubble">{message.text}</div>
                        )}
                      </>
                    )}
                  </div>
                  <button
                    type="button"
                    className="chat-message-actions"
                    onClick={(e) => toggleMenu(e, message)}
                    aria-label={t("message_actions")}
                    aria-haspopup="menu"
                  >
                    <FontAwesomeIcon icon={faEllipsisVertical} />
                  </button>
                </div>
                <div className="chat-meta">
                  {moment(message.createdAt).format("HH:mm")}
                  {message.editedAt && !message.deleted && (
                    <span className="chat-edited">
                      {" · "}
                      {t("edited")}
                    </span>
                  )}
                  {mine && message.id === lastOwnMessageId && (
                    <span className="chat-receipt">
                      {" · "}
                      {message.readAt ? t("seen") : t("delivered")}
                    </span>
                  )}
                </div>
              </div>
            </React.Fragment>
          );
        })}
        {activeContact && typing[activeContact.userId] && (
          <div className="chat-message theirs">
            <div className="chat-bubble chat-typing" aria-label={t("typing")}>
              <span />
              <span />
              <span />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMenu = () => {
    const message = menu.message;
    const mine = message.senderId === myId;
    return (
      <div
        className="chat-menu"
        role="menu"
        style={{ top: menu.top, left: menu.left, width: MENU_WIDTH }}
      >
        {mine && canEdit(message) && (
          <button type="button" role="menuitem" onClick={() => startEdit(message)}>
            <FontAwesomeIcon icon={faPen} />
            {t("edit")}
          </button>
        )}
        {!message.deleted && message.text && (
          <button type="button" role="menuitem" onClick={() => copyMessage(message)}>
            <FontAwesomeIcon icon={faCopy} />
            {t("copy")}
          </button>
        )}
        <button
          type="button"
          role="menuitem"
          className="danger"
          onClick={() => {
            setMenu(null);
            setConfirmDelete(message);
          }}
        >
          <FontAwesomeIcon icon={faTrash} />
          {t("delete")}
        </button>
      </div>
    );
  };

  const renderDeleteDialog = () => {
    const message = confirmDelete;
    const forEveryone = message.senderId === myId && !message.deleted;
    return (
      <div className="chat-dialog-backdrop" onClick={() => setConfirmDelete(null)}>
        <div
          className="chat-dialog"
          role="alertdialog"
          aria-label={t("delete_question")}
          onClick={(e) => e.stopPropagation()}
        >
          <p className="chat-dialog-title">{t("delete_question")}</p>
          {forEveryone && (
            <p className="chat-dialog-text">{t("delete_for_everyone_hint")}</p>
          )}
          {forEveryone && (
            <button
              type="button"
              className="chat-dialog-button danger"
              onClick={() => deleteMessage(message, "everyone")}
            >
              {t("delete_for_everyone")}
            </button>
          )}
          <button
            type="button"
            className="chat-dialog-button"
            onClick={() => deleteMessage(message, "me")}
          >
            {t("delete_for_me")}
          </button>
          <button
            type="button"
            className="chat-dialog-button muted"
            onClick={() => setConfirmDelete(null)}
            autoFocus
          >
            {t("cancel")}
          </button>
        </div>
      </div>
    );
  };

  const renderEditingBar = () => (
    <div className="chat-editing">
      <FontAwesomeIcon icon={faPen} className="chat-editing-icon" />
      <div className="chat-editing-text">
        <span className="chat-editing-title">{t("editing")}</span>
        <span className="chat-editing-original">
          {editing.text || (editing.attachment && editing.attachment.name)}
        </span>
      </div>
      <button
        type="button"
        className="chat-pending-remove"
        onClick={cancelEdit}
        aria-label={t("cancel_edit")}
      >
        <FontAwesomeIcon icon={faTimes} />
      </button>
    </div>
  );

  const renderPendingFile = () => (
    <div className="chat-pending">
      {pendingFile.previewUrl ? (
        <img src={pendingFile.previewUrl} alt="" className="chat-pending-thumb" />
      ) : (
        <FontAwesomeIcon
          icon={fileIcon(pendingFile.file.name)}
          className="chat-pending-icon"
        />
      )}
      <div className="chat-pending-text">
        <span className="chat-file-name">{pendingFile.file.name}</span>
        <span className="chat-file-size">
          {uploadProgress !== null
            ? `${t("uploading")} ${uploadProgress} %`
            : formatSize(pendingFile.file.size)}
        </span>
        {uploadProgress !== null && (
          <span className="chat-progress">
            <span style={{ width: `${uploadProgress}%` }} />
          </span>
        )}
      </div>
      {!sending && (
        <button
          type="button"
          className="chat-pending-remove"
          onClick={() => setPendingFile(null)}
          aria-label={t("remove_attachment")}
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      )}
    </div>
  );

  const renderComposer = () => (
    <form className="chat-composer" onSubmit={handleSend}>
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPT_ATTRIBUTE}
        onChange={handleFileInputChange}
        hidden
      />
      {!editing && (
        <button
          type="button"
          className="chat-attach"
          onClick={() => fileInputRef.current && fileInputRef.current.click()}
          disabled={sending}
          aria-label={t("attach")}
          title={t("attach_hint")}
        >
          <FontAwesomeIcon icon={faPaperclip} />
        </button>
      )}
      <textarea
        ref={textareaRef}
        rows={1}
        value={draft}
        placeholder={pendingFile ? t("caption_placeholder") : t("placeholder")}
        onChange={handleDraftChange}
        onKeyDown={handleKeyDown}
        onPaste={handlePaste}
        maxLength={MAX_LENGTH}
        aria-label={t("placeholder")}
      />
      <button
        type="submit"
        className="chat-send"
        disabled={
          (!draft.trim() && !pendingFile && !(editing && editing.attachment)) ||
          sending
        }
        aria-label={editing ? t("save") : t("send")}
      >
        <FontAwesomeIcon icon={editing ? faCheck : faPaperPlane} />
      </button>
    </form>
  );

  const renderNoCoach = () => (
    <div className="chat-no-coach">
      <FontAwesomeIcon icon={faComments} size="3x" />
      <p>{t("no_coach")}</p>
      <button type="button" className="chat-primary-button" onClick={openEmailContact}>
        {t("select_coach")}
      </button>
    </div>
  );

  return (
    <div className="chat-widget" ref={widgetRef}>
      {open && (
        <div
          ref={panelRef}
          className={`chat-panel ${dragActive ? "drag-active" : ""}`}
          role="dialog"
          aria-label={t("chat")}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
        >
          {dragActive && (
            <div className="chat-drop-overlay">
              <FontAwesomeIcon icon={faPaperclip} size="2x" />
              {t("drop_here")}
            </div>
          )}
          {renderHeader()}
          {isIndividual && !activeContact && renderNoCoach()}
          {!isIndividual && !activeContact && renderContactList()}
          {activeContact && renderMessages()}
          {activeContact && sendError && (
            <div className="chat-error">{t(sendError)}</div>
          )}
          {activeContact && pendingFile && renderPendingFile()}
          {activeContact && editing && renderEditingBar()}
          {menu && renderMenu()}
          {confirmDelete && renderDeleteDialog()}
          {activeContact && renderComposer()}
          {isIndividual && activeContact && (
            <button
              type="button"
              className="chat-email-link"
              onClick={openEmailContact}
            >
              <FontAwesomeIcon icon={faEnvelope} />
              {t("contact_by_email")}
            </button>
          )}
        </div>
      )}
      <button
        type="button"
        className={`chat-launcher ${open ? "open" : ""}`}
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
      >
        <span className="chat-launcher-icon">
          <FontAwesomeIcon icon={open ? faTimes : faComments} />
          {coach && (
            <span
              className={`chat-presence-dot ${coach.online ? "online" : ""}`}
            />
          )}
        </span>
        <span className="chat-launcher-label">
          {isIndividual ? t("chat_with_coach") : t("chat_with_clients")}
        </span>
        {totalUnread > 0 && !open && (
          <span className="chat-badge chat-launcher-badge">{totalUnread}</span>
        )}
      </button>
    </div>
  );
};

export default ChatWidget;
